import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TemplateBlocks } from '@engage/types';
import { apiClient } from './client';

export type TemplateCategory =
  | 'CANDIDATE_ENGAGEMENT'
  | 'BUSINESS_DEVELOPMENT'
  | 'JOB_CAMPAIGN'
  | 'DEAL_CAMPAIGN'
  | 'WEBINAR_EVENT'
  | 'NEWSLETTER';

export type TemplateTier = 'MINIMAL' | 'MEDIUM' | 'RICH';
export type TemplateKind = 'CONTENT_TEMPLATE' | 'LAYOUT_STRUCTURE' | 'CUSTOM_HTML';

export interface Template {
  id: string;
  org_id: string;
  name: string;
  subject: string;
  body_html: string;
  body_text: string;
  merge_fields: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
  // Templates Foundation (Sprint 14) — new fields on the wire.
  category: TemplateCategory | null;
  tier: TemplateTier | null;
  kind: TemplateKind;
  is_system: boolean;
  preheader: string | null;
  blocks: TemplateBlocks | null;
  // Builder UI (Sprint 15) — per-template tracking toggles.
  track_opens: boolean;
  track_clicks: boolean;
}

export interface TemplateWritable {
  name: string;
  subject: string;
  body_html?: string;
  body_text?: string;
  merge_fields?: string[];
  category?: TemplateCategory | null;
  tier?: TemplateTier | null;
  kind?: TemplateKind;
  preheader?: string | null;
  /** Block tree — when present, the server re-renders body_html / body_text
   *  from it on save and computes merge fields from the rendered output. */
  blocks?: TemplateBlocks | null;
  track_opens?: boolean;
  track_clicks?: boolean;
}

export interface TemplatesListParams {
  category?: TemplateCategory;
  kind?: TemplateKind;
  /** Server default is true when omitted. */
  include_system?: boolean;
}

export function useTemplates(params: TemplatesListParams = {}) {
  return useQuery<{ data: Template[] }>({
    queryKey: ['templates', params],
    queryFn: async () => {
      const search: Record<string, string> = {};
      if (params.category) search.category = params.category;
      if (params.kind) search.kind = params.kind;
      if (params.include_system !== undefined) search.include_system = String(params.include_system);
      const { data } = await apiClient.get<{ data: Template[] }>('/templates', { params: search });
      return data;
    },
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery<Template>({
    queryKey: ['templates', id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: Template }>(`/templates/${id}`);
      return data.data;
    },
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TemplateWritable) => {
      const { data } = await apiClient.post<{ data: Template }>('/templates', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useUpdateTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<TemplateWritable>) => {
      const { data } = await apiClient.patch<{ data: Template }>(`/templates/${id}`, payload);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      qc.invalidateQueries({ queryKey: ['templates', id] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

/**
 * Clone-on-edit: copies a (typically system) template into the caller's
 * org as an editable copy. Returns the new id, which the caller usually
 * navigates to via `/templates/:id/edit`.
 */
export function useCloneTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { id: string; name?: string }) => {
      const { data } = await apiClient.post<{ data: Template }>(`/templates/${params.id}/clone`, {
        name: params.name,
      });
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// ── Preview + send-test (Sprint 15) ───────────────────────────────────────

export interface PreviewSample {
  id: string;
  label: string;
  description: string;
}

export interface PreviewResult {
  html: string;
  text: string;
  merge_fields: string[];
  sample_id: string | null;
  injected_footer: boolean;
}

export interface PreviewParams {
  blocks: TemplateBlocks;
  settings?: TemplateBlocks['settings'];
  brand_kit_override?: Record<string, unknown>;
  sample_id?: string;
}

export function usePreviewSamples() {
  return useQuery<{ data: PreviewSample[] }>({
    queryKey: ['templates', 'preview-samples'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: PreviewSample[] }>('/templates/preview-samples');
      return data;
    },
    staleTime: Infinity, // Sample list is effectively static.
  });
}

/**
 * Live preview render. Debounce the call site (~300ms) — every keystroke
 * in the builder shouldn't fire a request.
 */
export function usePreviewTemplate() {
  return useMutation({
    mutationFn: async (params: PreviewParams) => {
      const { data } = await apiClient.post<{ data: PreviewResult }>('/templates/preview', params);
      return data.data;
    },
  });
}

export interface SendTestResult {
  sent: boolean;
  message_id: string;
  to: string;
}

export function useSendTestTemplate(id: string) {
  return useMutation({
    mutationFn: async (params: { to: string; sample_id?: string }) => {
      const { data } = await apiClient.post<{ data: SendTestResult }>(
        `/templates/${id}/send-test`,
        params,
      );
      return data.data;
    },
  });
}

// ── AI generate (Option 2) ─────────────────────────────────────────────────

export interface AiGenerateResult {
  subject: string;
  preheader: string;
  body_html: string;
  body_text: string;
}

/** Generate an email-safe HTML draft from a natural-language brief. The result
 *  is a draft — the caller reviews/edits it in the Custom HTML editor and saves
 *  it as a CUSTOM_HTML template. Server sanitises the HTML before returning. */
export function useGenerateEmailTemplate() {
  return useMutation({
    mutationFn: async (params: { prompt: string; tone?: string }) => {
      const { data } = await apiClient.post<{ data: AiGenerateResult }>(
        '/templates/ai-generate',
        params,
      );
      return data.data;
    },
  });
}

// ── Canva import (Option 3) ────────────────────────────────────────────────

export interface CanvaImportResult {
  subject: string;
  preheader: string;
  body_html: string;
  body_text: string;
  images_rehosted: number;
  images_skipped: number;
}

/** Import a Canva email — either a "HTML and images" ZIP (file) or pasted HTML.
 *  Images are re-hosted on Engage's public store and the HTML is sanitised
 *  server-side. Returns a draft for the Custom HTML editor. */
export function useImportCanva() {
  return useMutation({
    mutationFn: async (params: { file?: File; html?: string }) => {
      if (params.file) {
        const form = new FormData();
        form.append('file', params.file);
        const { data } = await apiClient.post<{ data: CanvaImportResult }>(
          '/templates/import-canva',
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return data.data;
      }
      const { data } = await apiClient.post<{ data: CanvaImportResult }>(
        '/templates/import-canva',
        { html: params.html ?? '' },
      );
      return data.data;
    },
  });
}
